import { Box, Collapse, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import Header from "../../common/Header";
import CustomTextField from "../../common/CustomTextField";
import { AddCircleOutlineOutlined, DoDisturbAltOutlined, ModeOutlined, SearchOutlined, CloudDownload, DownloadOutlined, KeyboardArrowDown, ExitToApp} from "@mui/icons-material";
import Card from "../../common/Card";
import { useForm } from "react-hook-form";
import Button from "../../common/Button";
import Table, { Row } from "../../common/CustomTable";
import { useLocation, useNavigate } from "react-router-dom";
import LegacyToolBar from "../../common/LegacyToolBar";
import CustomPatternTextField from "../../common/CustomPatternTextField";
import { SearchAgency, clearAgencyInfoList, searchAgency, useAgencyReducer, Option, getAgencyInfoById, getPracticeListByUserRoleInAgency } from "../../../redux/slice/agency/AgencySlice";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAppDispatch } from "../../../redux/slice/redux-hooks";
import { formatAddress, getCodeTableDisplayLabel, hideElement, isIsrCommissionType, sortNames } from "../../../utils/Utility";
import { createListSort } from "../../../utils/listSort";
import CustomIconButton from "../../common/CustomIconButton";
import { useSelector } from "react-redux";
import { RootState } from "../../../redux/store";
import { selectIsValueFoundInToken, useAuthSlice } from "../../../redux/slice/authSlice";
import { AGENCY_STATUS, CAN_ACCESS_APPLICATION, COMMISSION_TYPE_CODETABLE, EXPORTABLE_ITEMS, USER_ROLE_ENTITY_NAMES, USER_ROLES } from "../../../constants/applicationConstants";
import CustomDropdownField from "../../common/CustomDropdownFiled";
import { getSearchCriteriaFromSessionStorage, saveSelectionForPage, setSearchCriteriaToSessionStorage } from "../../../utils/StorageUtils";
import OptionsPopup from "../../common/OptionsPopup";
import DeleteAgencyPopup from "../../common/DeleteAgencyPopup";
import { SUCCESS_CODE } from "../../../service/ServiceConstants";
import dayjs from "dayjs";
import { AgencyInfo, fetchAgenciesList, setSingleAlertObj, showHideLoadingDialog, SingleAlertInfo, useCommonReducer } from "../../../redux/slice/commonSlice";
import { useNavigation } from "../../../hooks/useNavigation";
import { useFilterPanelFocus } from "../../../hooks/useFilterPanelFocus";
import ExportToExcel from "../../common/ExportToExcel";
import { MenuItem, ListItemIcon } from "@mui/material";
import StyledMenu from "../../common/StyledMenu";
import { useModalStyles } from "../../common/useModalStyles";
import { getCodeTableValuesByTableName, useCodetablesSlice } from "../../../redux/slice/codetableSlice";

interface Column {
  id: string;
  label: string;
  minWidth?: number;
  align?: "right" | "center" | "left";
  format?: (value: number) => string;
}

const columns: Column[] = [
  { id: "name", label: "Name", minWidth: 200 },
  { id: "phone", label: "Phone", minWidth: 100 },
  { id: "address", label: "Address", minWidth: 250 },
  { id: "commType", label: "Service Fee Type", minWidth: 40 },
  { id: "status", label: "Status", minWidth: 40 },
  { id: "action", label: "Action", minWidth: 50, },
];

const agencyStatusOptions: Option[] = [
  { label: "Active", value: "Active" },
  { label: "Inactive", value: "Inactive" }
]

interface AgencyRow {
  id: number | undefined;
  name: string | undefined;
  phone: string | undefined;
  email: string | undefined;
  address: string | undefined;
  commType: string | undefined;
  agencyStatus?: string | undefined;
  inactiveDate?: any;
  action?: JSX.Element;
}

const ITEMS_PER_PAGE = 20;

const agencyListSort = createListSort(
  ["agencyName", "commissionType", "agencyStatus"] as const,
  {
    name: "agencyName",
    commType: "commissionType",
    status: "agencyStatus",
  }
);

const AgencyList = () => {

  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const location = useLocation();
  const { navigate } = useNavigation();
  const dispatch = useAppDispatch();
  const { primaryBtnSx, tokens: modalTokens } = useModalStyles();

  const { control, handleSubmit, reset, setValue } = useForm<SearchAgency>();
  const [showCard, setShowCard] = useState(false);
  const {
    filterPanelRef,
    toggleFilterPanel,
    handleFilterPanelEntering,
    handleFilterPanelEntered,
    handleFilterPanelExited,
  } = useFilterPanelFocus(showCard, setShowCard);
  const [showDeleteAgencyPopup, setShowDeleteAgencyPopup] = useState(false);
  const [agencyName, setAgencyName] = useState("");
  const [agencyData, setAgencyData] = useState("");
  const [agencyId, setAgencyId] = useState(0);
  const [searchCriteria, setSearchCriteria] = useState<SearchAgency>({
    agencyName: undefined,
    phone: undefined,
    email: undefined,
    address: undefined,
    commType: undefined,
    agencyStatus: "Active",
    currentPage: 1,
    itemsPerPage: ITEMS_PER_PAGE
  });

  const { userInfo } = useAuthSlice();
  const { agenciesInfoList, AgencyInactivateStatus } = useAgencyReducer();
  const { agencyInfoList } = useCommonReducer();
  const { allCodeTablesValuesMap } = useCodetablesSlice();

  const commissionTypes = useMemo(() => {
    if (!allCodeTablesValuesMap) return [];

    const values = getCodeTableValuesByTableName(
      allCodeTablesValuesMap,
      COMMISSION_TYPE_CODETABLE
    );
    return Array.isArray(values) ? values : [];
  }, [allCodeTablesValuesMap]);

  const commTypeOptions: Option[] = useMemo(
    () =>
      commissionTypes
        .filter((item) => !isIsrCommissionType(allCodeTablesValuesMap, item.keyName))
        .map((item) => ({
          label: item.displayValue,
          value: item.keyName,
        })),
    [allCodeTablesValuesMap, commissionTypes]
  );

  const commissionTypeLabel = useCallback(
    (commissionType: string | null | undefined) =>
      getCodeTableDisplayLabel(
        allCodeTablesValuesMap,
        COMMISSION_TYPE_CODETABLE,
        commissionType
      ),
    [allCodeTablesValuesMap]
  );

  const canCreateAgency = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAgencyActions", AGENCY_STATUS.CREATE)(state));
  const canEditAgency = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAgencyActions", AGENCY_STATUS.EDIT)(state));
  const canInactivateAgency = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAgencyActions", AGENCY_STATUS.INACTIVATE)(state));
  const canAccessAgencyStatusSearchFilter = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessAgencyActions", AGENCY_STATUS.INACTIVATE)(state));
  
  const [anchorElMenu, setAnchorElMenu] = useState<null | HTMLElement>(null);
  const [fetchPracticesCompleted, setFetchPracticesCompleted] = useState<boolean>(false);

  const [activeButtonId, setActiveButtonId] = useState<string | null>(null); // CS-1555
  const canExportAgencyList = useSelector((state: RootState) => selectIsValueFoundInToken("canAccessExportExcel", EXPORTABLE_ITEMS.AGENCY)(state));

  const listBorderColor = isDark ? "rgba(148,163,184,0.2)" : "rgba(15,23,42,0.12)";
  const listPanelBg    = isDark ? "#141720" : "rgba(255,255,255,0.82)";
  const listHoverBg    = isDark ? "rgba(20,184,166,0.09)" : "rgba(20,184,166,0.06)";

  const exportBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#14B8A6",
    borderColor: "rgba(20,184,166,0.55)",
    backgroundColor: theme.palette.mode === "dark" ? "rgba(20,184,166,0.08)" : "rgba(20,184,166,0.04)",
    whiteSpace: "nowrap",
    "& .MuiSvgIcon-root": {
      color: "#14B8A6",
    },
    "&:hover": {
      color: "#fff",
      borderColor: "#14B8A6",
      backgroundColor: "#14B8A6",
      boxShadow: "0 6px 22px rgba(20,184,166,0.28)",
      "& .MuiSvgIcon-root": {
        color: "#fff",
      },
    },
  };

  const listCardSx = {
    borderRadius: "14px",
    border: `1px solid ${listBorderColor}`,
    backgroundColor: listPanelBg,
    backgroundImage: "none",
    backdropFilter: "blur(8px)",
    boxShadow: isDark ? "0 12px 32px rgba(0,0,0,0.28)" : "0 12px 28px rgba(15,23,42,0.08)",
  };

  const filterPanelSx = {
    ...listCardSx,
    p: { xs: "14px !important", md: "18px !important" },
    position: "relative",
    zIndex: 20,
    overflow: "visible",
    "& .MuiAutocomplete-root, & .MuiFormControl-root": { width: "100%" },
    "& .MuiTextField-root": { height: "42px", minHeight: "42px !important" },
    "& .MuiOutlinedInput-root, & .MuiInputBase-root, & .MuiAutocomplete-inputRoot, & .MuiAutocomplete-root .MuiOutlinedInput-root": {
      minHeight: "42px !important",
      height: "42px !important",
      borderRadius: "10px !important",
      backgroundColor: `${modalTokens.inputBg} !important`,
      color: modalTokens.textPrimary,
      fontSize: "0.88rem",
      boxShadow: isDark ? "none" : "0 1px 3px rgba(0,0,0,0.06)",
      alignItems: "center",
      boxSizing: "border-box",
    },
    "& .MuiAutocomplete-root .MuiOutlinedInput-root": { padding: "0 38px 0 12px !important" },
    "& .MuiAutocomplete-root .MuiInputBase-input, & .MuiAutocomplete-input, & .MuiOutlinedInput-input": {
      paddingTop: "8px !important",
      paddingBottom: "8px !important",
      height: "24px !important",
      lineHeight: "24px !important",
      boxSizing: "border-box",
    },
    "& .MuiOutlinedInput-input": { paddingLeft: "12px !important", paddingRight: "12px !important" },
    "& .MuiAutocomplete-endAdornment": { top: "50% !important", transform: "translateY(-50%)", right: "8px !important" },
    "& .MuiOutlinedInput-notchedOutline, & .MuiOutlinedInput-root fieldset": { borderColor: `${modalTokens.inputBorder} !important` },
    "& .MuiOutlinedInput-root:hover fieldset, & .MuiOutlinedInput-root.Mui-focused fieldset": { borderColor: `${modalTokens.accent} !important`, borderWidth: "1px !important" },
    "& .MuiOutlinedInput-root.Mui-focused": { boxShadow: isDark ? "none" : "0 0 0 3px rgba(20,184,166,0.12)" },
    "& .MuiInputBase-input": { color: modalTokens.textPrimary, fontSize: "0.88rem" },
    "& input::selection": { color: modalTokens.textPrimary, backgroundColor: isDark ? "rgba(20,184,166,0.28)" : "rgba(20,184,166,0.2)" },
    "& .MuiOutlinedInput-root:has(input:-webkit-autofill)": { backgroundColor: "#376c9f !important", "& fieldset": { borderColor: "#7fb1df !important" } },
    "& input:-webkit-autofill, & input:-webkit-autofill:hover, & input:-webkit-autofill:focus": { WebkitTextFillColor: "#ffffff", caretColor: "#ffffff", WebkitBoxShadow: "0 0 0 100px #376c9f inset", borderRadius: "10px", transition: "background-color 5000s ease-in-out 0s" },
    "& .MuiInputLabel-root": { color: isDark ? "rgba(255,255,255,0.5)" : "rgba(0,0,0,0.45)", fontSize: "0.88rem" },
    "& .MuiInputLabel-root.Mui-focused": { color: `${modalTokens.accent} !important` },
    "& .MuiInputLabel-outlined:not(.MuiInputLabel-shrink)": { transform: "translate(14px, 9px) scale(1)" },
    "& .MuiChip-root": { borderRadius: "999px", backgroundColor: isDark ? "rgba(20,184,166,0.16)" : "rgba(20,184,166,0.1)", color: modalTokens.accent, border: `1px solid ${isDark ? "rgba(20,184,166,0.26)" : "rgba(20,184,166,0.18)"}`, fontWeight: 600 },
    "& .MuiSvgIcon-root": { color: modalTokens.textSecondary },
  };

  const primaryActionBtnSx = {
    ...primaryBtnSx,
    color: "#fff",
    "& .MuiTypography-root": { color: "#fff", fontWeight: 700 },
    "& .MuiButton-startIcon": { color: "#fff" },
  };

  const outlineBtnSx = {
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "10px",
    py: 1.05,
    px: 1.6,
    color: "#00B8D9",
    borderColor: "rgba(0,184,217,0.55)",
    backgroundColor: isDark ? "rgba(0,184,217,0.08)" : "rgba(0,184,217,0.04)",
    whiteSpace: "nowrap",
    "&:hover": { color: "#fff", borderColor: "#00B8D9", backgroundColor: "#00B8D9", boxShadow: "0 6px 22px rgba(0,184,217,0.28)" },
  };

  const exportMenuItemBtnSx = {
    justifyContent: "flex-start",
    textTransform: "none",
    fontWeight: 700,
    fontSize: "0.86rem",
    borderRadius: "8px",
    py: 1,
    px: 1.4,
    color: "#2dd4bf",
    borderColor: "rgba(45,212,191,0.35)",
    backgroundColor: isDark ? "rgba(20,184,166,0.13)" : "rgba(20,184,166,0.08)",
    boxShadow: "none",
    "& .MuiButton-startIcon": { mr: 1, "& svg": { fontSize: "18px" } },
    "&:hover": { color: "#fff", borderColor: "#2f9b8f", backgroundColor: "#2f9b8f", boxShadow: "0 10px 24px rgba(20,184,166,0.28)" },
    "&.Mui-disabled": { color: isDark ? "rgba(148,163,184,0.55)" : "rgba(100,116,139,0.7)", borderColor: isDark ? "rgba(148,163,184,0.18)" : "rgba(100,116,139,0.24)", backgroundColor: isDark ? "rgba(148,163,184,0.06)" : "rgba(100,116,139,0.06)" },
  };

  const agencyTableSx = {
    "& .MuiTableContainer-root": { borderRadius: "12px", border: "none", backgroundColor: "transparent" },
    "& table": {  borderCollapse: "collapse", borderSpacing: 0 },
    "& .MuiTableCell-root": { borderBottom: `1px solid ${isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)"}`, py: 1.15, fontSize: "0.84rem" },
    "& .MuiTableHead-root .MuiTableCell-root": { height: "42px"  },
    "& .MuiTableHead-root .MuiTypography-root": { color: "#00B8D9 !important", fontSize: "0.78rem", fontWeight: 800, letterSpacing: "0.02em" },
    "& .MuiTableBody-root .MuiTableRow-root": { transition: "background-color 0.18s ease, box-shadow 0.18s ease" },
    "& .MuiTableBody-root .MuiTableRow-root:hover": { backgroundColor: `${listHoverBg} !important` },
    "& .MuiTableBody-root .MuiTableCell-root, & .MuiTableBody-root .MuiTypography-root": { color: modalTokens.textPrimary, fontSize: "0.84rem" },
    "& .MuiIconButton-root": { color: isDark ? "#94a3b8" : "#475569", borderRadius: "8px", "&:hover": { color: modalTokens.accent, backgroundColor: isDark ? "rgba(20,184,166,0.12)" : "rgba(20,184,166,0.08)" } },
    "& .MuiPaginationItem-root": { borderColor: listBorderColor, color: modalTokens.textSecondary, borderRadius: "8px", "&.Mui-selected": { color: "#fff", backgroundColor: modalTokens.accent, borderColor: modalTokens.accent } },
  };

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElMenu(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorElMenu(null);
  };

  // CS-431 Start ->  Functionality added to delete the Agency
  const handleActionDeleteAgency = async (row: any) => {
    const { id } = row;
    const response = await dispatch(getAgencyInfoById(id));
    if (response?.payload?.code === SUCCESS_CODE && response?.payload?.data) {
      setAgencyData(response.payload.data);
      setAgencyName(response.payload.data?.agencyName);
      setAgencyId(response.payload.data?.agencyId);
    }
    clickShowInactivateAgencyPopup();
  }

  const clickShowInactivateAgencyPopup = () => {
    setShowDeleteAgencyPopup(true);
  }

  useEffect(()=>{
    if(AgencyInactivateStatus==="success"){
      let successObj: SingleAlertInfo = {
        message: "Agency is inactivated successfully.  We have also inactivated respective Users (if any).",
        alertType: "success"
      };
      dispatch(setSingleAlertObj(successObj));

      dispatch(searchAgency(searchCriteria))
    }

  },[AgencyInactivateStatus])
  // CS-431 End

  //Mount
  useEffect(() => {
    dispatch(showHideLoadingDialog(true))
  }, []);

  //Unmount
  useEffect(() => () => {
    dispatch(clearAgencyInfoList());
  }, []);

  useEffect(() => {
    //1. When login another user pagination is remain in the history and can redirect that page
    let criteria = getSearchCriteriaFromSessionStorage(CAN_ACCESS_APPLICATION.AGENCY_MANAGEMENT + "SearchCriteria" + userInfo.userId);
    if ((location.state && location.state.agencyListSearchCriteria) || (location.state && location.state.withSearchHistory && criteria)) {
      if (location.state && location.state.withSearchHistory) {
        //Below navigate line call back to this useEffect - This for refreshing issue when navigate with state.withSearchHistory=true
        navigate("/agency_list", { state: { agencyListSearchCriteria: criteria } });
        return;
      }
      let searchCriteria: SearchAgency = {
        agencyName: '',
        phone: '',
        email: '',
        address: '',
        commType: '',
        agencyStatus: '',
        currentPage: 0,
        itemsPerPage: 0
      };
      if (location.state && location.state.agencyListSearchCriteria) {
        searchCriteria = location.state.agencyListSearchCriteria;
      } else {
        searchCriteria = criteria ? criteria : searchCriteria;
      }
      setSearchCriteria(searchCriteria);
      setValuesToCriteriaFields(searchCriteria);

      searchAgenciesList(searchCriteria);
    } else {
      if (userInfo.userRole === USER_ROLES.ROLE_PRACTICE_USER_PRU) {
        dispatch(showHideLoadingDialog(false))
      } else {
        loadInitialAgencyList(1);
      }
    }
  }, [location.state]);

  const setValuesToCriteriaFields = (searchCriteria: SearchAgency) => {
    setValue("agencyName", searchCriteria.agencyName);
    setValue("phone", searchCriteria.phone);
    setValue("email", searchCriteria.email);
    setValue("address", searchCriteria.address);
    setValue("commType", searchCriteria.commType);
    setValue("agencyStatus", searchCriteria.agencyStatus);
  }

  const loadInitialAgencyList = (pNumber: number = 1) => {
    const data: SearchAgency = {
      agencyName: '',
      phone: '',
      email: '',
      address: '',
      commType: '',
      agencyStatus: 'Active',
      currentPage: pNumber,
      itemsPerPage: ITEMS_PER_PAGE,
    };
    setSearchCriteria(data);
    setValuesToCriteriaFields(data);

    searchAgenciesList(data);
  }

  const searchAgenciesList = async (searchCriteria: SearchAgency) => {
    const normalizedCriteria = agencyListSort.normalize(searchCriteria);
    setSearchCriteriaToSessionStorage(CAN_ACCESS_APPLICATION.AGENCY_MANAGEMENT + "SearchCriteria" + userInfo.userId, normalizedCriteria);

    let dataToSearch = {
      ...normalizedCriteria,
    }

    // dispatch(searchAgency(dataToSearch));
    // setFetchPracticesCompleted(false);
    // CS-1572 - Start
    if (!fetchPracticesCompleted) {
      const res = await dispatch(getPracticeListByUserRoleInAgency(true)).unwrap();
      if (res) {
        setFetchPracticesCompleted(true);
      }
    }
    dispatch(searchAgency(dataToSearch));
    // CS-1572 - End
  }

  const onSubmit = (data: SearchAgency) => {
    const data2: SearchAgency = {
      agencyName: data.agencyName,
      phone: data.phone,
      email: data.email,
      address: data.address,
      commType: data.commType,
      agencyStatus: data.agencyStatus,
      currentPage: 1,
      itemsPerPage: ITEMS_PER_PAGE,
      ...agencyListSort.pick(searchCriteria),
    }
    setSearchCriteria(data2);

    //Below navigate line call to useEffect[location.state] and search
    navigate("/agency_list", { state: { agencyListSearchCriteria: data2 } });
  }

  const handlePagination = (pageNumber: number) => {
    const data = {
      ...searchCriteria,
      currentPage: pageNumber + 1
    }
    setSearchCriteria(data);

    //Below navigate line call to useEffect[location.state] and search
    navigate("/agency_list", { state: { agencyListSearchCriteria: data } });
  }

  const handleTableSort = (columnId: string, order: "asc" | "desc") => {
    const data = agencyListSort.withSort(searchCriteria, columnId, order);
    if (!data) {
      return;
    }
    setSearchCriteria(data);
    navigate("/agency_list", { state: { agencyListSearchCriteria: data } });
  }

  const handleExport = (exportType: string) => {
    const searchCriteriaFromStorage = getSearchCriteriaFromSessionStorage(
      CAN_ACCESS_APPLICATION.AGENCY_MANAGEMENT +
      "SearchCriteria" +
      userInfo.userId
    );

    if (searchCriteriaFromStorage) {
      let dataToSearch = {
        ...searchCriteria,
        currentPage: searchCriteriaFromStorage.currentPage,
        downloadState: true
      };

      let exportTypeConstant = EXPORTABLE_ITEMS.AGENCY;
      if (exportType === "searchList") {
        exportTypeConstant = EXPORTABLE_ITEMS.AGENCY_LIST;
      } else if (exportType === "details") {
        exportTypeConstant = EXPORTABLE_ITEMS.AGENCY_DETAILS;
      }

      ExportToExcel(
        dataToSearch,
        columns,
        dispatch,
        exportTypeConstant
      );
    }
    handleMenuClose();
  };

  const handleActionEdit = (row: AgencyRow) => {
    navigate("/agency_form", { state: { agency: { agencyId: row.id } } });
  }

  // CS-1555- Start
  const handleActionButtonClick = (buttonId: string, originalClick: () => void) => {
    const currentPageKey = "agencyList";
    saveSelectionForPage(currentPageKey, buttonId);
    setActiveButtonId(buttonId);
    sessionStorage.setItem("activeActionButtonId", buttonId);
    originalClick();
  };

  const handleRowNavigateToEdit = (row: Row) => {
    if (!canEditAgency) return;
    const agencyId = String((row as any).id);
    handleActionButtonClick(`agencyListActionButton-${agencyId}`, () => {
      navigate("/agency_form", { state: { agency: { agencyId: (row as any).id } } });
    });
  };
  // CS-1555- End

  const actionComponent = (row: AgencyRow): JSX.Element | null => {

    if (!canEditAgency && !canInactivateAgency) {
      return null;
    }

    return (
      <>
        <CustomIconButton
          color="info"
          size="small"
          variant="outlined"
          onClick={() => handleActionButtonClick(`agencyListActionButton-${row.id}`, () => handleActionEdit(row))} // CS-1555
          tooltipMessage="Edit"
          tooltipPlacement="top"
          style={{ display: `${hideElement(!canEditAgency)}` }}
        >
          <ModeOutlined fontSize="small" />
        </CustomIconButton>
        {canInactivateAgency && row.inactiveDate === null && (
          <CustomIconButton
            color="error"
            size="small"
            variant="outlined"
            onClick={() => handleActionButtonClick(`agencyListActionButton-${row.id}`, () => handleActionDeleteAgency(row))} // CS-1555
            tooltipMessage="Inactivate Agency"
            tooltipPlacement="top"
            // style={{ display: `${hideElement(!canInactivateAgency)}` }}
            style={{ display: `${hideElement(row && dayjs(row.inactiveDate).isValid()) && canInactivateAgency}` }}

          >
            <DoDisturbAltOutlined fontSize="small" />
          </CustomIconButton>
        )}
      </>
    );
  };

  const agencyList: AgencyRow[] = useMemo(() => {
    if (agenciesInfoList?.content) {
      return agenciesInfoList.content?.map((agency) => {
        return {
          id: agency.agencyId,
          name: agency.agencyName,
          phone: agency.phone,
          email: agency.email,
          address: formatAddress(
            agency.streetAddress,
            agency.unitNumber,
            agency.city,
            agency.state,
            agency.zipcode
          ),
          commType: commissionTypeLabel(agency.commissionType),
          status: agency.inactiveDate ? "INACTIVE" : "ACTIVE",
          inactiveDate: agency.inactiveDate
        };
      });
    }
    return [];
  }, [agenciesInfoList, commissionTypeLabel]);

    const agencyNameDropdown = useMemo(() => {
      let options: Option[] = [];
  
      if (agencyInfoList && Array.isArray(agencyInfoList)) {
        
        const sortedAgencyList = sortNames(agencyInfoList, 'agencyName');
  
        sortedAgencyList.forEach((agency: AgencyInfo) => {
          options.push({ label: agency.agencyName, value: agency.agencyName });
        });
      }
      return options;
    }, [agencyInfoList]);

  const legacyItems = [{ icon: <ModeOutlined color="info" />, text: "Edit" }];

  useEffect(() => {
    dispatch(fetchAgenciesList());
  }, []);

  useEffect(() => {
    if (agenciesInfoList && fetchPracticesCompleted) {
      dispatch(showHideLoadingDialog(false))
    }
  }, [agenciesInfoList, fetchPracticesCompleted])

  const totalRecords = agenciesInfoList?.totalElements ?? 0;

  return (
    <>
      {showDeleteAgencyPopup &&
        <DeleteAgencyPopup
          open={showDeleteAgencyPopup}
          agencyData={agencyData}
          onClose={() => {
            setShowDeleteAgencyPopup(false);
            setAgencyName("");
            setAgencyId(0)
          }}
        />
      }

      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          px: { xs: 1, md: 3 },
          py: 2,
        }}
      >
        {/* -- Filter panel -- */}
        <Box sx={{ position: "relative", zIndex: 20, overflow: "visible" }}>
          <Collapse in={showCard} ref={filterPanelRef} onEntering={handleFilterPanelEntering} onEntered={handleFilterPanelEntered} onExited={handleFilterPanelExited}>
            <Card sx={filterPanelSx}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item md={4} xs={12}>
                    <CustomDropdownField
                      name="agencyName"
                      control={control}
                      options={agencyNameDropdown}
                      label="Agency Name"
                      placeholderText="Select agency"
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <CustomPatternTextField
                      control={control}
                      name="phone"
                      label="Phone"
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="email"
                      label="Email"
                      placeholder="Enter email"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={4} xs={12}>
                    <CustomTextField
                      control={control}
                      controllerName="address"
                      label="Address"
                      placeholder="Enter address"
                      size="small"
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    <CustomDropdownField
                      control={control}
                      name="commType"
                      label="Service Fee Type"
                      placeholderText="Select service fee type"
                      options={commTypeOptions}
                    />
                  </Grid>
                  <Grid item md={3} xs={12}>
                    {userInfo.entityName === USER_ROLE_ENTITY_NAMES.ADMIN && (
                      <CustomDropdownField
                        control={control}
                        name="agencyStatus"
                        label="Agency Status"
                        options={agencyStatusOptions}
                      />
                    )}
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      fullWidth
                      size="large"
                      startIcon={<SearchOutlined />}
                      sx={{ ...primaryActionBtnSx, height: "42px", py: 0 }}
                      type="submit"
                      removeUpperCase
                    >
                      Search
                    </Button>
                  </Grid>
                </Grid>
              </form>
            </Card>
          </Collapse>
        </Box>

        {/* -- Main list card -- */}
        <Box mt={2} sx={{ position: "relative", zIndex: 1 }}>
          <Card sx={{ ...listCardSx, p: { xs: "14px !important", md: "18px !important" } }}>
            <Grid container justifyContent="flex-end" alignItems="center" spacing={1.2}>
              <Grid item xs={12} md>
                <Typography variant="h3" fontWeight={800} color="text.primary">Agency List</Typography>
              </Grid>

              {/* Show / Hide filters */}
              <Grid item xs={12} md="auto">
                <Button
                  variant="outlined"
                  color="info"
                  onClick={toggleFilterPanel}
                  startIcon={<SearchOutlined />}
                  fullWidth
                  sx={outlineBtnSx}
                  removeUpperCase
                >
                  {showCard ? "Hide Filters" : "Show Filters"}
                </Button>
              </Grid>

              {/* Create Agency */}
              {canCreateAgency && (
                <Grid item xs={12} md="auto">
                  <Button
                    fullWidth
                    startIcon={<AddCircleOutlineOutlined />}
                    onClick={() => navigate("/agency_form")}
                    sx={primaryActionBtnSx}
                    removeUpperCase
                  >
                    Create Agency
                  </Button>
                </Grid>
              )}

              {/* CS-1156 - Export */}
              {canExportAgencyList && (
                <Grid item xs={12} md="auto">
                  {!isMobile && (
                    <Button
                      id="customized-button"
                      aria-controls={Boolean(anchorElMenu) ? "customized-menu" : undefined}
                      aria-haspopup="true"
                      aria-expanded={Boolean(anchorElMenu) ? "true" : undefined}
                      variant="outlined"
                      disableElevation
                      onClick={handleMenuClick}
                      startIcon={<ExitToApp />}
                      sx={exportBtnSx}
                      removeUpperCase
                      fullWidth
                      endIcon={
                        <Box
                          component="span"
                          sx={{
                            display: "flex",
                            transition: "transform 0.3s ease",
                            transform: anchorElMenu ? "rotate(180deg)" : "rotate(0deg)",
                            padding: "2px",
                          }}
                        >
                          <KeyboardArrowDown />
                        </Box>
                      }
                    >
                      Export
                    </Button>
                  )}

                  {isMobile && (
                    <Button
                      color="success"
                      variant="text"
                      onClick={(event) => handleMenuClick(event)}
                      startIcon={<DownloadOutlined fontSize="small" />}
                      sx={exportBtnSx}
                      removeUpperCase
                    >
                      Export
                    </Button>
                  )}

                  <StyledMenu
                    id="customized-menu"
                    MenuListProps={{ "aria-labelledby": "customized-button" }}
                    anchorEl={anchorElMenu}
                    open={Boolean(anchorElMenu)}
                    onClose={handleMenuClose}
                    slotProps={{
                      paper: {
                        sx: {
                          width: "260px",
                          maxWidth: "90vw",
                          mt: 0.75,
                          p: 0.75,
                          borderRadius: "12px",
                          border: `1px solid ${isDark ? "rgba(0,184,217,0.3)" : "rgba(0,184,217,0.22)"}`,
                          backgroundColor: isDark ? "#141720" : "#ffffff",
                          backgroundImage: "none",
                          boxShadow: isDark
                            ? "0 18px 42px rgba(0,0,0,0.45)"
                            : "0 14px 32px rgba(15,23,42,0.14)",
                        },
                      },
                    }}
                  >
                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={() => handleExport("searchList")}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Search List
                      </Button>
                    </Box>

                    <Box sx={{ px: 1, mb: 1 }}>
                      <Button
                        fullWidth
                        variant="outlined"
                        startIcon={<CloudDownload />}
                        onClick={() => handleExport("details")}
                        disabled={totalRecords > 1000}
                        sx={exportMenuItemBtnSx}
                        removeUpperCase
                      >
                        Export Details List
                      </Button>
                    </Box>

                    {totalRecords > 1000 && (
                      <Box
                        sx={{
                          mx: 1,
                          p: 1.5,
                          mt: 1,
                          backgroundColor: "warning.light",
                          borderRadius: 1,
                          color: "warning.contrastText",
                        }}
                      >
                        <Typography variant="body2">
                          Export is available only when the list contains 1,000 records or fewer.
                          Maximum export size: 1000 records.
                        </Typography>
                      </Box>
                    )}
                  </StyledMenu>
                </Grid>
              )}
              {/* CS-1156 - End */}
            </Grid>

            <Table
              columns={columns}
              rows={fetchPracticesCompleted ? agencyList : []}
              actionComponent={actionComponent}
              totalPages={fetchPracticesCompleted ? agenciesInfoList?.totalPages : 0}
              pageNumber={handlePagination}
              totalElementText={"Records: " + ((fetchPracticesCompleted && agenciesInfoList?.totalElements) ? agenciesInfoList?.totalElements : "0")}
              currentPageNumber={searchCriteria.currentPage - 1}
              showMenu={true}
              sortableColumns={agencyListSort.sortableColumns}
              serverSideSorting
              onSortChange={handleTableSort}
              controlledSortColumn={agencyListSort.controlledColumn(searchCriteria.sortBy)}
              controlledSortOrder={searchCriteria.sortOrder ?? null}
              // CS-1555 - Start
              currentPageKey={"agencyList"}
              uniqueValue="id"
              activeButtonId={activeButtonId}
              onRowClick={handleRowNavigateToEdit}
              clickableCards={true}
              // CS-1555 - End
              sx={agencyTableSx}
            />
          </Card>
        </Box>
      </Box>
    </>
  );

}

export default AgencyList;